import * as Sentry from "@sentry/react";

import React, { useState } from "react";
import { useForm } from "react-hook-form";
import clsx from "clsx";
import { CircleLoader } from "@/components/ui/circleLoader";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { resetPassword } from "@/views/auth/api";

interface FormInputs {
  username: string;
}

export const ResetPasswordForm: React.FC = () => {
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormInputs>();

  const onSubmit = async (payload: FormInputs) => {
    try {
      await resetPassword(payload.username);
      setIsSuccess(true);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  return (
    <form className="space-y-6" onSubmit={handleSubmit(onSubmit)} method="post">
      {isSuccess && (
        <Alert variant="success">
          <AlertDescription>
            A link to reset your password has been sent to your email. Please check your inbox or spam folder and follow
            the instructions.
          </AlertDescription>
        </Alert>
      )}
      <div>
        <label htmlFor="username" className="block text-sm font-medium text-ui-paragraph-text">
          Username
        </label>
        <div className="mt-1">
          <input
            id="username"
            type="text"
            required
            {...register("username", { required: "This is required." })}
            className={clsx(
              "block w-full appearance-none rounded-md border border-input px-3 py-2 placeholder-muted shadow-sm focus:border-primary focus:outline-none focus:ring-primary sm:text-sm",
              {
                "border-red-500": !!errors.username,
              },
            )}
          />
          {!!errors.username && <p className="mt-2 text-sm text-red-500">{errors.username.message}</p>}
        </div>
      </div>

      <div>
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex w-full justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting && <CircleLoader />}
          {isSubmitting ? "Loading..." : "Reset password"}
        </button>
      </div>
    </form>
  );
};
